import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";

import useAgencyDomain from "../../../datahooks/useAgencyDomain";
import useAgencyDomainConfiguration from "../../../datahooks/useAgencyDomainConfiguration";
import useDeploymentAliases from "../../../datahooks/useDeploymentAliases";
import useDeploymentAliasesMutations from "../../../datahooks/useDeploymentAliasesMutations";
import DNSRecordsTable from "./DNSRecordsTable";

const commonAlertClassName = "mr-auto max-w-lg";

export default function DomainStatusAlert({
  domainName,
}: {
  domainName: string;
}) {
  //the vercel domain associated with the custom domain
  const { agencyDomain, agencyDomainError } = useAgencyDomain(domainName);
  const {
    agencyDomainConfiguration,
    isFetchingAgencyDomainConfiguration,
    refetchAgencyDomainConfiguration,
    agencyDomainConfigurationError,
  } = useAgencyDomainConfiguration(domainName);
  const { deploymentAliases } = useDeploymentAliases();
  const { addDeploymentAlias, isAddingDeploymentAlias } =
    useDeploymentAliasesMutations();

  if (agencyDomainError || agencyDomainConfigurationError) {
    return (
      <Alert variant="danger" className={commonAlertClassName}>
        Error loading domain configuration
      </Alert>
    );
  }
  if (agencyDomain && agencyDomainConfiguration) {
    const { recommendedIPv4, recommendedCNAME } = agencyDomainConfiguration;

    const domainComponents = domainName.split(".");
    const dnsRecords =
      domainComponents.length === 2
        ? [
            {
              type: "A",
              name: "@",
              value: recommendedIPv4[0].value[0],
            },
          ]
        : [
            {
              type: "CNAME",
              name: domainComponents[0],
              value: recommendedCNAME[0].value,
            },
          ];

    if (agencyDomain.verified === false) {
      return (
        <Alert variant="warning">
          Domain requires verification. Add the following records to verify
          ownership. You can remove the records after verification is complete
          <DNSRecordsTable
            records={[
              ...dnsRecords,
              {
                type: agencyDomain.verification[0].type,
                name: "_vercel",
                value: agencyDomain.verification[0].value,
              },
            ]}
          />
          <p className="text-black-700 mt-1.5">
            Note: It can take up to 48h for changes to your DNS configuration to
            propagate
          </p>
        </Alert>
      );
    }
    if (agencyDomainConfiguration.misconfigured) {
      if (domainName.endsWith(import.meta.env.VITE_WHITE_LABEL_DOMAIN)) {
        return (
          <Alert variant="default" className={commonAlertClassName}>
            <div className="flex flex-col gap-y-2.5">
              We are setting up your domain, it will be live shortly
              <Button
                size="sm"
                className="ml-auto"
                onClick={() => refetchAgencyDomainConfiguration()}
                isLoading={isFetchingAgencyDomainConfiguration}
              >
                Refresh
              </Button>
            </div>
          </Alert>
        );
      }
      return (
        <Alert variant="warning">
          Invalid DNS configuration. Update the DNS records at your provider to
          match the following records
          <DNSRecordsTable records={dnsRecords} />
          <p className="text-black-700 mt-1.5">
            Note: It can take up to 48h for changes to your DNS configuration to
            propagate
          </p>
        </Alert>
      );
    }
  }

  if (
    import.meta.env.MODE !== "production" &&
    deploymentAliases &&
    !deploymentAliases.find((alias) => alias === domainName)
  ) {
    return (
      <Alert variant="warning" className={commonAlertClassName}>
        <div className="flex flex-col gap-y-2.5">
          Domain is not connected to the correct dev environment
          <Button
            size="sm"
            className="ml-auto"
            isLoading={isAddingDeploymentAlias}
            onClick={() => addDeploymentAlias({ domainName })}
          >
            Connect
          </Button>
        </div>
      </Alert>
    );
  }
}
